import { useCallback, useEffect, useState } from "react";
import { readThemeCookie, type Theme, themeCookie } from "../lib/sharedTheme";

export type { Theme };

const STORAGE_KEY = "ztcTheme";

const readInitialTheme = (): Theme => {
  if (typeof window === "undefined") return "dark";
  const shared = readThemeCookie(document.cookie);
  if (shared) return shared;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  const prefersLight = window.matchMedia?.("(prefers-color-scheme: light)").matches;
  return prefersLight ? "light" : "dark";
};

const applyTheme = (theme: Theme): void => {
  document.documentElement.dataset.theme = theme;
};

export type UseThemeResult = {
  theme: Theme;
  setTheme: (next: Theme) => void;
  toggle: () => void;
};

export const useTheme = (): UseThemeResult => {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
    // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API isn't in every browser yet.
    document.cookie = themeCookie(theme, window.location.hostname, window.location.protocol === "https:");
  }, [theme]);

  // Follow a change made in the marketing admin when this tab comes back into focus.
  useEffect(() => {
    const sync = (): void => {
      const shared = readThemeCookie(document.cookie);
      if (shared) setThemeState(shared);
    };
    window.addEventListener("focus", sync);
    return () => window.removeEventListener("focus", sync);
  }, []);

  const setTheme = useCallback((next: Theme): void => {
    setThemeState(next);
  }, []);

  const toggle = useCallback((): void => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return { theme, setTheme, toggle };
};
